import {
  getPredictionOutcomeEventID,
  SecurityTypesUtils,
  type IPredictionOutcomeEvent,
  type Security,
  type TreeRow,
} from '@talos/kyoko';
import type { ValueFormatterParams } from 'ag-grid-community';
import { isPredictionOutcomeAvailableOnMarket } from '../../../PredictionMarkets/PredictionCard/predictionCardMarkets';
import { PriceGridEntity, PriceGridRowKind } from '../../PriceGridEntity';
import type { PriceGridBucket, PriceGridSourceItem } from '../../types';

function getEventLabel(event: IPredictionOutcomeEvent) {
  return event.EventName || event.EventSymbol || event.EventID;
}

export class PredictionMarketEventRow extends PriceGridEntity implements TreeRow<PredictionMarketsPriceGridRow> {
  readonly event: IPredictionOutcomeEvent;

  constructor({
    event,
    representativeSourceItem,
  }: {
    event: IPredictionOutcomeEvent;
    representativeSourceItem: PriceGridSourceItem;
  }) {
    super({
      sourceItem: representativeSourceItem,
      bucket: { id: 'event' },
      kind: PriceGridRowKind.Child,
      rowID: `prediction-event:${event.EventID}`,
      dataPath: [event.EventID],
    });
    this.event = event;
  }

  get label() {
    return getEventLabel(this.event);
  }

  groupColumnValueGetter() {
    return this.label;
  }

  groupColumnValueFormatter({ value }: ValueFormatterParams<PredictionMarketsPriceGridRow, string>) {
    return value ?? '';
  }

  groupColumnFilterValueGetter() {
    return this.label;
  }

  // PriceGridEntity.clone drops this subclass. Enrichment clones visible rows, and price cells match on it.
  override clone() {
    const clone = new PredictionMarketEventRow({
      event: this.event,
      representativeSourceItem: this.sourceItem,
    });
    clone.addEnrichments(this.getEnrichments());
    return clone;
  }
}

export class PredictionMarketOutcomeRow extends PriceGridEntity implements TreeRow<PredictionMarketsPriceGridRow> {
  readonly event: IPredictionOutcomeEvent;

  constructor({
    sourceItem,
    bucket,
    kind,
    dataPath,
    event,
  }: {
    sourceItem: PriceGridSourceItem;
    bucket: PriceGridBucket;
    kind: PriceGridRowKind;
    dataPath: string[];
    event: IPredictionOutcomeEvent;
  }) {
    super({
      sourceItem,
      bucket,
      kind,
      rowID: `${sourceItem.id ?? sourceItem.security.Symbol}|${bucket.id}`,
      dataPath,
    });
    this.event = event;
  }

  get label() {
    const outcomeLabel = this.sourceItem.security.DisplaySymbol || this.sourceItem.security.Symbol;
    return this.kind === PriceGridRowKind.Parent ? outcomeLabel : '';
  }

  get filterValue() {
    return this.kind === PriceGridRowKind.Parent
      ? `${getEventLabel(this.event)} ${this.label} ${this.sourceItem.security.Symbol}`
      : '';
  }

  get expiration() {
    return this.kind === PriceGridRowKind.Parent ? this.sourceItem.security.Expiration : undefined;
  }

  groupColumnValueGetter() {
    return this.label;
  }

  groupColumnValueFormatter({ value }: ValueFormatterParams<PredictionMarketsPriceGridRow, string>) {
    return value ?? '';
  }

  groupColumnFilterValueGetter() {
    return this.filterValue;
  }

  // PriceGridEntity.clone drops this subclass. Enrichment clones visible rows, and price cells match on it.
  override clone() {
    const clone = new PredictionMarketOutcomeRow({
      sourceItem: this.sourceItem,
      bucket: this.bucket,
      kind: this.kind,
      dataPath: this.dataPath,
      event: this.event,
    });
    clone.addEnrichments(this.getEnrichments());
    return clone;
  }
}

export type PredictionMarketsPriceGridRow = PredictionMarketEventRow | PredictionMarketOutcomeRow;

function getBuckets(sourceItem: PriceGridSourceItem): PriceGridBucket[] {
  const sizeBuckets = sourceItem.security.SizeBuckets?.filter(Boolean) ?? [];
  const sizes = sizeBuckets.length > 0 ? sizeBuckets : [sourceItem.security.NormalSize].filter(Boolean);

  return sizes.map((size, index) => ({
    id: sizeBuckets.length > 0 ? `bucket-${index}` : 'normal-size',
    size,
  }));
}

export function getPredictionMarketEvent(
  sourceItem: PriceGridSourceItem,
  eventsByID: Map<string, IPredictionOutcomeEvent>
): IPredictionOutcomeEvent | undefined {
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(sourceItem.security)) {
    return undefined;
  }

  const eventID = getPredictionOutcomeEventID(sourceItem.security);
  return eventID ? eventsByID.get(eventID) : undefined;
}

function buildPredictionMarketOutcomeRows({
  sourceItem,
  event,
}: {
  sourceItem: PriceGridSourceItem;
  event: IPredictionOutcomeEvent;
}): PredictionMarketOutcomeRow[] {
  const buckets = getBuckets(sourceItem);
  const parentPath = [event.EventID, sourceItem.security.Symbol];

  return buckets.map((bucket, index) => {
    const kind = index === 0 ? PriceGridRowKind.Parent : PriceGridRowKind.Child;
    return new PredictionMarketOutcomeRow({
      sourceItem,
      bucket,
      kind,
      dataPath:
        kind === PriceGridRowKind.Parent ? parentPath : [...parentPath, `${sourceItem.security.Symbol}|${bucket.id}`],
      event,
    });
  });
}

export function buildPredictionMarketsPriceGridRows({
  securities,
  eventsByID,
  market,
}: {
  securities: Iterable<Security>;
  eventsByID: Map<string, IPredictionOutcomeEvent>;
  market?: string;
}): PredictionMarketsPriceGridRow[] {
  const rows: PredictionMarketsPriceGridRow[] = [];
  const addedEventIDs = new Set<string>();

  for (const security of securities) {
    if (!isPredictionOutcomeAvailableOnMarket(security, market)) {
      continue;
    }
    const sourceItem = { security };
    const event = getPredictionMarketEvent(sourceItem, eventsByID);
    if (!event) {
      continue;
    }

    const eventID = event.EventID;
    if (!addedEventIDs.has(eventID)) {
      rows.push(new PredictionMarketEventRow({ event, representativeSourceItem: sourceItem }));
      addedEventIDs.add(eventID);
    }

    rows.push(...buildPredictionMarketOutcomeRows({ sourceItem, event }));
  }

  return rows;
}
